import { createAsyncThunk } from "@reduxjs/toolkit";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { GlobalService, HttpService } from "../../modules";
import { ServiceArea, ServiceAreaType } from "../../modules/admin/models/ServiceArea.model";
import i18n from "../../i18n";

// Types for action parameters
export interface ServiceAreaParams {
    page: number;
    limit: number;
    isShowLoader?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    type?: string;
    country?: string;
}

export interface CreateServiceAreaData {
    name: string;
    type: { id: number };
    radius: number;
    restrictions: string[];
    cities: string[];
    postalCodes: string[];
    country: { id: string };
    serviceTypes: string[];
    emergencyServices: boolean;
    province: string;
}

export interface UpdateServiceAreaData extends CreateServiceAreaData {
    id: string;
}

// Get All Service Areas
export const getAllServiceAreas = createAsyncThunk<any, ServiceAreaParams>(
    "serviceArea/getAllServiceAreas",
    async ({ page, limit, isShowLoader = true, search, sortBy = "createdAt", sortOrder = "desc", type, country }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());
            params.append("sortBy", sortBy);
            params.append("sortOrder", sortOrder);
            
            if (search && search.trim()) {
                params.append("keyword", search.trim());
            }
            
            if (type && type !== "all") {
                params.append("type", type);
            }
            
            if (country && country !== "all") {
                params.append("country", country);
            }
            
            const response = await HttpService.get(
                `api/v1/service-areas?${params.toString()}`,
                true,
                {}
            );
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Service Areas failed";

            return rejectWithValue(errorMessage);
        }
    }
);

// Create Service Area
export const createServiceArea = createAsyncThunk<any, { data: CreateServiceAreaData }>(
    "serviceArea/createServiceArea",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/service-areas",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.serviceArea.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Service Area creation failed";

            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Update Service Area
export const updateServiceArea = createAsyncThunk<any, { id: string; data: CreateServiceAreaData }>(
    "serviceArea/updateServiceArea",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const response = await HttpService.patch(
                `api/v1/service-areas/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", "Success", "Service Area Updated Successfully");
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Service Area update failed";

            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Delete Service Area
export const deleteServiceArea = createAsyncThunk<any, { id: string }>(
    "serviceArea/deleteServiceArea",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/service-areas/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            
            dispatch(hideLoader());
            GlobalService.showToast("success", "Success", "Service Area deleted Successfully");
            
            // Return the ID so the reducer can remove the item from state
            return { id, response };
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Service Area deletion failed";

            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get Service Area by ID
export const getServiceAreaById = createAsyncThunk<any, { id: string }>(
    "serviceArea/getServiceAreaById",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/service-areas/${id}`,
                true,
                {}
            );

            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Service Area failed";

            return rejectWithValue(errorMessage);
        }
    }
);

// Get Provinces and Cities from Service Areas
export const getProvincesAndCities = createAsyncThunk<any, { countryId?: string; countryCode?: string; isShowLoader?: boolean }>(
    "serviceArea/getProvincesAndCities",
    async ({ countryId, countryCode, isShowLoader = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const params = new URLSearchParams();
            if (countryId && countryId.trim()) {
                params.append("countryId", countryId.trim());
            }
            if (countryCode && countryCode.trim()) {
                params.append("countryCode", countryCode.trim());
            }

            const response = await HttpService.get(
                `api/v1/service-areas/locations/provinces-cities${params.toString() ? `?${params.toString()}` : ""}`,
                true,
                {}
            );

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Provinces and Cities failed";

            return rejectWithValue(errorMessage);
        }
    }
);
